import { useTranslation } from "@redotech/redo-i18n";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { Team } from "@redotech/redo-team-common/team";
import { Edge, MarkerType, Node } from "@xyflow/react";
import { MutableRefObject, useMemo, useRef } from "react";
import { WarningStep } from "../components/flow-left-panel";
import { createEdgesFromSteps } from "../utils/layout/edges-from-steps";
import {
  createNodesFromSteps,
  FlowEditorNodeData,
} from "../utils/layout/nodes-from-steps";
import { buildPhantomPhaseEdges } from "../utils/layout/phantom-phase-edges";

export const PATH_HIGHLIGHT_COLOR = "#17b26a";

interface HighlightParams {
  highlightedNodeIds: Set<string>;
  currentStepId?: string;
  highlightedEdgePairs?: Set<string>;
}

interface UseFlowNodesAndEdgesParams {
  steps: Map<StepId, Step>;
  flowType: FlowType;
  nodePositions: Map<string, { x: number; y: number }>;
  selectedNodeIdsRef: MutableRefObject<Set<string>>;
  team?: Team;
  highlight?: HighlightParams;
  warningSteps?: WarningStep[];
  isStepLocked?: (stepId: StepId) => boolean;
  hasLockedSection?: boolean;
}

interface UseFlowNodesAndEdgesResult {
  nodes: Node<FlowEditorNodeData>[];
  edges: Edge[];
  computedPositions: Map<string, { x: number; y: number }>;
}

export function useFlowNodesAndEdges({
  steps,
  flowType,
  nodePositions,
  selectedNodeIdsRef,
  team,
  highlight,
  warningSteps,
  isStepLocked,
  hasLockedSection,
}: UseFlowNodesAndEdgesParams): UseFlowNodesAndEdgesResult {
  // Snapshot of each step's last successfully-positioned (x,y). Used so a
  // step that becomes temporarily orphaned (e.g. edge re-routed mid-edit)
  // stays where the user last saw it instead of jumping to dagre's default.
  const { i18n } = useTranslation();
  const lastKnownPositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );

  return useMemo(() => {
    void i18n.language;
    if (steps.size === 0) {
      return {
        nodes: [] as Node<FlowEditorNodeData>[],
        edges: [] as Edge[],
        computedPositions: new Map(),
      };
    }

    const {
      nodes: positionedNodes,
      nodePositions: computedPositions,
      phaseLayout,
    } = createNodesFromSteps(
      steps,
      flowType,
      nodePositions,
      selectedNodeIdsRef,
      team,
      warningSteps,
      lastKnownPositionsRef.current,
      isStepLocked,
      hasLockedSection,
    );

    // Refresh the cache with the positions this render settled on, scoped to
    // steps currently in the model so removed steps drop out naturally.
    const refreshed = new Map<string, { x: number; y: number }>();
    for (const node of positionedNodes) {
      refreshed.set(node.id, node.position);
    }
    lastKnownPositionsRef.current = refreshed;

    const validNodeIds = new Set(positionedNodes.map((node) => node.id));

    const stepEdges = createEdgesFromSteps(
      steps,
      computedPositions,
      selectedNodeIdsRef,
      validNodeIds,
    );
    const phantomEdges = buildPhantomPhaseEdges(steps, phaseLayout);
    const rawEdges = [...stepEdges, ...phantomEdges];

    if (!highlight) {
      return { nodes: positionedNodes, edges: rawEdges, computedPositions };
    }

    const nodes = positionedNodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        pathHighlighted: highlight.highlightedNodeIds.has(node.id),
        isCurrentStep: node.id === highlight.currentStepId,
      },
    }));

    const edges = highlight.highlightedEdgePairs
      ? rawEdges.map((edge) => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const isHighlighted = highlight.highlightedEdgePairs!.has(
            `${edge.source}-${edge.target}`,
          );
          if (!isHighlighted) {
            return edge;
          }
          return {
            ...edge,
            style: { stroke: PATH_HIGHLIGHT_COLOR, strokeWidth: 2.5 },
            markerEnd: { type: MarkerType.Arrow, color: PATH_HIGHLIGHT_COLOR },
          };
        })
      : rawEdges;

    return { nodes, edges, computedPositions };
  }, [
    steps,
    flowType,
    nodePositions,
    selectedNodeIdsRef,
    team,
    highlight,
    warningSteps,
    isStepLocked,
    hasLockedSection,
    i18n.language,
  ]);
}
